import { Component, computed, ElementRef, inject, input, signal, ViewChild } from '@angular/core';
import { LpCanvasService } from '@core/lp/lp-canvas.service';
import { CanvasScrollerComponent } from '@features/canvas/canvas-scroller/canvas-scroller.component';
import { LpCanvasElementComponent } from '@features/lp/lp-canvas/lp-canvas-element/lp-canvas-element.component';
import { LpCanvasRendererArrowsComponent } from '@features/lp/lp-canvas/lp-canvas-renderer-arrows/lp-canvas-renderer-arrows.component';
import { Contexts } from '@features/lp/lp-canvas/lp-canvas-renderer/canvas-contexts';
import { useElementSize } from '@shared/angular.utils';
import { generateUid } from '@shared/misc.utils';
import { one } from '@shared/vector.utils';
import { LpCanvasElement } from '@typings/lp/generated';
import { Vector2, Vector2CanvasNormalized } from '@typings/vectors';

@Component({
  selector: 'lp-canvas-renderer',
  imports: [CanvasScrollerComponent, LpCanvasElementComponent, LpCanvasRendererArrowsComponent],
  templateUrl: './lp-canvas-renderer.component.html',
  styleUrl: './lp-canvas-renderer.component.css',
})
export class LpCanvasRendererComponent {
  elements = input<LpCanvasElement[]>();
  size = input<Vector2CanvasNormalized>(one() as Vector2CanvasNormalized);
  sizeInPixels = useElementSize();

  // PANNING
  origin = signal([0, 0] as Vector2CanvasNormalized);
  @ViewChild('canvas', { read: ElementRef })
  canvasEl!: ElementRef<HTMLElement>;
  // CONTEXTS
  contexts = new Contexts();
  sanitizedElements = computed(() => {
    return (this.elements() ?? []).map((el) => {
      const copy = structuredClone(el);
      const hasParallaxComponent = copy.components.some((c) => c.dataType === 'ParallaxCanvasComponent');
      if (!hasParallaxComponent) {
        copy.components.unshift({ dataType: 'ParallaxCanvasComponent', speed: 1, uid: generateUid() });
      }
      return copy;
    });
  });
  readonly #lpCanvasService = inject(LpCanvasService);

  getElementBounds(element: LpCanvasElement): { position: Vector2CanvasNormalized; size: Vector2CanvasNormalized } {
    const shapeProperties = this.#lpCanvasService.getShapeProperties(element.shape, this.sizeInPixels());

    const ctx = this.contexts.get(element.uid);
    const pos = ctx.positionTransformers.reduce((old, transformer) => transformer(old, this.origin()), [
      element.position.x + shapeProperties.offset[0],
      element.position.y + shapeProperties.offset[1],
    ] as Vector2CanvasNormalized);

    return { position: pos, size: shapeProperties.size };
  }

  handlePanned(origin: Vector2) {
    const normalized = this.getNormalizedPositionFromPixels(origin[0], origin[1]);
    this.origin.set(normalized);
  }

  private getNormalizedPositionFromPixels(x: number, y: number): Vector2CanvasNormalized {
    const canvasEl = this.canvasEl.nativeElement;
    const rect = canvasEl.getBoundingClientRect();
    return [x / rect.width, y / rect.height] as Vector2CanvasNormalized;
  }
}
